import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import SPCChart from './components/SPCChart';
import HistogramChart from './components/HistogramChart';
import BaselineLogButton from './components/BaselineLogButton';

// ─── Typography shorthand ────────────────────────────────────────────────────
const FONT = "'CPF Imm Sook', sans-serif";

// ─── Helpers ─────────────────────────────────────────────────────────────────
/** Determine status-circle color from Cpk when backend alert color is unavailable */
// const cpkColor = (cpk) => {
//   if (cpk >= 1.33) return '#4caf50'; // green – capable
//   if (cpk >= 1.0)  return '#2196f3'; // blue  – adequate
//   if (cpk >= 0.67) return '#ffc107'; // amber – marginal
//   return '#f44336';                  // red   – out of control
// };
const cpkColor = (cpk) => {
  if (cpk < 0.6) return '#f44336'; // red – capable
  if (cpk >= 0.6 && cpk <= 0.99) return '#ffc107'; // amber – marginal
  return '#4caf50';                  // green   – out of control
};
// ─── Reusable small card ──────────────────────────────────────────────────────
const SectionCard = ({ children, sx = {}, accent }) => (
  <Box
    sx={{
      bgcolor: '#0d0d0d',
      border: '1px solid #1e1e1e',
      borderLeft: accent ? `3px solid ${accent}` : '1px solid #1e1e1e',
      borderRadius: '8px',
      p: '7px 12px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Label / value pair used in LSL / Std / USL row */
const SpecCell = ({ label, value }) => (
  <Box sx={{ textAlign: 'center', flex: 1 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#aaa', letterSpacing: 1 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 32, fontWeight: 'bold', color: '#fff', lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);

/** Cpk / Ppk value block (right side of right panel) */
// const CapabilityBlock = ({ label, value, target, color }) => {
//   const blockColor = color ?? (value >= target ? '#4caf50' : '#ffc107');
//   return (
//     <Box sx={{ textAlign: 'center', mb: 0.5 }}>
//       <Typography
//         sx={{ fontFamily: FONT, fontSize: 16, color: blockColor, fontWeight: 'bold', letterSpacing: 1 }}
//       >
//         {label}
//       </Typography>
//       <Typography sx={{ fontFamily: FONT, fontSize: 44, fontWeight: 'bold', color: blockColor, lineHeight: 1 }}>
//         {value.toFixed(2)}
//       </Typography>
//       <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa' }}>
//         Target &gt; {target.toFixed(1)}
//       </Typography>
//     </Box>
//   );
// };
const CapabilityBlock = ({ label, value, target, color }) => {
  const hasData = value != null && Number.isFinite(Number(value));
  const blockColor = hasData ? color : '#757575';

  return (
    <Box sx={{ textAlign: 'center', mb: 0.5 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 16, color: blockColor, fontWeight: 'bold', letterSpacing: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 44, fontWeight: 'bold', color: blockColor, lineHeight: 1 }}>
        {hasData ? Number(value).toFixed(2) : '-'}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa' }}>
        Target &gt; {target.toFixed(1)}
      </Typography>
    </Box>
  );
};
// ─── Mock / default data ─────────────────────────────────────────────────────
const buildSmoothGaussianCurve = (
  points = [],
  mean,
  step = 0.1
) => {
  const validPoints = points
    .map((point) => ({
      x: Number(point?.x),
      y: Number(point?.y),
    }))
    .filter(
      (point) =>
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        point.y >= 0
    )
    .sort((a, b) => a.x - b.x);

  if (validPoints.length < 2 || !Number.isFinite(Number(mean))) {
    return validPoints;
  }

  const mu = Number(mean);
  const totalY = validPoints.reduce(
    (sum, point) => sum + point.y,
    0
  );

  if (totalY <= 0) {
    return validPoints;
  }

  // ประมาณค่า Sigma จากจุด Curve ที่ Backend ส่งมา
  const variance =
    validPoints.reduce(
      (sum, point) =>
        sum + point.y * ((point.x - mu) ** 2),
      0
    ) / totalY;

  const sigma = Math.sqrt(variance);

  if (!Number.isFinite(sigma) || sigma <= 0) {
    return validPoints;
  }

  // หาความสูงของ Curve ที่เหมาะกับข้อมูลต้นทาง
  const gaussianValues = validPoints.map((point) =>
    Math.exp(
      -0.5 * ((point.x - mu) / sigma) ** 2
    )
  );

  const numerator = validPoints.reduce(
    (sum, point, index) =>
      sum + point.y * gaussianValues[index],
    0
  );

  const denominator = gaussianValues.reduce(
    (sum, value) => sum + value ** 2,
    0
  );

  const amplitude =
    denominator > 0
      ? numerator / denominator
      : Math.max(...validPoints.map((point) => point.y));

  const xMin = validPoints[0].x;
  const xMax = validPoints[validPoints.length - 1].x;
  const smoothPoints = [];

  for (let x = xMin; x <= xMax + step / 2; x += step) {
    smoothPoints.push({
      x: Number(x.toFixed(2)),
      y:
        amplitude *
        Math.exp(
          -0.5 * ((x - mu) / sigma) ** 2
        ),
    });
  }

  return smoothPoints;
};

// ─── Main page component ──────────────────────────────────────────────────────
const SigmaTrackGiveawayPage1 = () => {
  const {
    lsl = 0, usl = 0,
    ucl = null, lcl = null, meanX = null, processCapChart = [],
    fsData, rawWeights = [],
    avgWeight = 0, cpk5 = null, ppk5 = null, sigma5 = 0,
    totalPack = 0, onSpecPack = 0, upperPack = 0, detectedPack = 0,
    giveawayPct = 0, giveawayKg = 0, giveawayTarget = 1,
    timeRange = '--:-- - --:--', skuLabel = '--',
    lineName = '--', plantName = '--',
    alertColor = null,
    suggestionWeb = '',
    showSuggestionWeb = true,
    shift = '', factoryDate = '', baselineParams = null, baselineLabel = '', baselineCurrentShiftCount = null,
  } = useOutletContext() ?? {};

  const cpk = cpk5;  // page 1 uses 5-min Cpk
  const ppk = ppk5;

  const cpkTarget = 1.0;
  const ppkTarget = 1.0;

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const formatDateTime = (d) => {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const mon = MONTHS[d.getMonth()];
    const yr = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${day} ${mon} ${yr}   ${hh}:${mm}:${ss}`;
  };

  const formatUnixTime = (timestamp) => {
    if (timestamp === null || timestamp === undefined) return '';
    const numericTimestamp = Number(timestamp);
    const milliseconds =
      numericTimestamp < 1e12
        ? numericTimestamp * 1000
        : numericTimestamp;

    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(milliseconds));
  };

  // ── SPC Chart — use XMR_POINTS directly (stored xi per bucket) ──────────
  const { spcData, spcCategories } = useMemo(() => {
    const xmrPoints = fsData?.XMR_POINTS;
    if (Array.isArray(xmrPoints) && xmrPoints.length) {
      const sorted = [...xmrPoints].sort((a, b) => a.t - b.t);
      return {
        spcData: sorted.map(({ xi }) => parseFloat((xi ?? 0).toFixed(2))),
        spcCategories: sorted.map(({ t }) => formatUnixTime(t)),
      };
    }
    if (!fsData || !rawWeights.length) return { spcData: [], spcCategories: [] };
    // fallback: group rawWeights by 5-min bucket
    const buckets = {};
    rawWeights.forEach(({ t, w }) => {
      const key = Math.floor(t / 300);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(w);
    });
    const sorted = Object.entries(buckets).sort(([a], [b]) => +a - +b);
    return {
      spcData: sorted.map(([, ws]) => parseFloat((ws.reduce((a, b) => a + b, 0) / ws.length).toFixed(2))),
      spcCategories: sorted.map(([timestamp]) => formatUnixTime(Number(timestamp) * 300)),
    };
  }, [rawWeights, fsData]);

  const processCapChartData = useMemo(() => {
    if (!Array.isArray(processCapChart) || processCapChart.length === 0) {
      return {
        histData: [],
        withinCurve: [],
        overallCurve: [],
        chartLsl: null,
        chartUsl: null,
        chartAvg: null,
        chartXMin: undefined,
        chartXMax: undefined,
      };
    }

    // กรองเฉพาะแถวที่มี bin_center ถูกต้อง และเรียงจากน้อยไปมาก
    const validRows = processCapChart
      .filter((row) => Number.isFinite(Number(row?.bin_center)))
      .sort(
        (a, b) =>
          Number(a.bin_center) - Number(b.bin_center)
      );

    if (validRows.length === 0) {
      return {
        histData: [],
        withinCurve: [],
        overallCurve: [],
        chartLsl: null,
        chartUsl: null,
        chartAvg: null,
        chartXMin: undefined,
        chartXMax: undefined,
      };
    }

    const firstRow = validRows[0];

    const chartLsl = Number(firstRow.lsl);
    const chartUsl = Number(firstRow.usl);
    const chartAvg = Number(firstRow.avg);

    // Histogram: bin_center เป็นแกน X และ count เป็นแกน Y
    const histData = validRows.map((row) => {
      const x = Number(row.bin_center);
      const count = Number(row.count);

      return {
        x,
        y: Number.isFinite(count) ? count : 0,
        fillColor:
          Number.isFinite(chartLsl) &&
            Number.isFinite(chartUsl) &&
            x >= chartLsl &&
            x <= chartUsl
            ? '#5b9bd5'
            : '#f4434699',
      };
    });

    // เส้นทึบ: Normal curve ระยะสั้น
    const withinCurve = validRows
      .filter(
        (row) =>
          Number.isFinite(Number(row.bin_center)) &&
          Number.isFinite(Number(row.y_within))
      )
      .map((row) => ({
        x: Number(row.bin_center),
        y: Number(row.y_within),
      }));

    // เส้นประ: Normal curve ระยะยาว
    const overallCurve = validRows
      .filter(
        (row) =>
          Number.isFinite(Number(row.bin_center)) &&
          Number.isFinite(Number(row.y_overall))
      )
      .map((row) => ({
        x: Number(row.bin_center),
        y: Number(row.y_overall),
      }));

    const lowEdges = validRows
      .map((row) => Number(row.low_edge))
      .filter(Number.isFinite);

    const binCenters = validRows
      .map((row) => Number(row.bin_center))
      .filter(Number.isFinite);

    // หาความกว้างของแต่ละ Bin
    const binWidth =
      binCenters.length > 1
        ? Math.abs(binCenters[1] - binCenters[0])
        : 1;

    const minimumLowEdge =
      lowEdges.length > 0
        ? Math.min(...lowEdges)
        : Math.min(...binCenters) - binWidth / 2;

    const maximumBinEdge =
      Math.max(...binCenters) + binWidth / 2;

    return {
      histData,
      withinCurve,
      overallCurve,
      chartLsl: Number.isFinite(chartLsl) ? chartLsl : null,
      chartUsl: Number.isFinite(chartUsl) ? chartUsl : null,
      chartAvg: Number.isFinite(chartAvg) ? chartAvg : null,
      chartXMin: minimumLowEdge,
      chartXMax: maximumBinEdge,
    };
  }, [processCapChart]);

  const {
    histData,
    withinCurve,
    overallCurve,
    chartAvg,
  } = processCapChartData;

  // เพิ่มความละเอียดของ y_within ให้เป็น Gaussian Curve
  const smoothWithinCurve = useMemo(
    () =>
      buildSmoothGaussianCurve(
        withinCurve,
        chartAvg,
        0.1
      ),
    [withinCurve, chartAvg]
  );

  // เพิ่มความละเอียดของ y_overall ให้เป็น Gaussian Curve
  const smoothOverallCurve = useMemo(
    () =>
      buildSmoothGaussianCurve(
        overallCurve,
        chartAvg,
        0.1
      ),
    [overallCurve, chartAvg]
  );

  const NO_DATA_COLOR = '#757575';

  const hasHistogramData = useMemo(() => {
    return histData.some((point) => {
      const y = Number(point?.y);
      return Number.isFinite(y) && y > 0;
    });
  }, [histData]);

  // เลือกค่าที่แย่ที่สุดระหว่าง Cpk และ Ppk
  const worstCapabilityValue = useMemo(() => {
    const values = [cpk, ppk]
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(Number)
      .filter(Number.isFinite);

    return values.length ? Math.min(...values) : null;
  }, [cpk, ppk]);

  const worstColor = (...colors) => {
    const values = colors.map((color) => String(color || '').toUpperCase());

    if (values.some((color) => color === 'RED' || color === '#F44336')) return '#f44336';
    if (values.some((color) => color === 'YELLOW' || color === '#FFC107')) return '#ffc107';
    return '#4caf50';
  };

  const avgStatusColor =
    !hasHistogramData || worstCapabilityValue === null
      ? NO_DATA_COLOR
      : worstColor(alertColor, cpkColor(worstCapabilityValue));

  const hasCpkData = hasHistogramData && cpk != null && cpk !== '' && Number.isFinite(Number(cpk));
  const hasPpkData = hasHistogramData && ppk != null && ppk !== '' && Number.isFinite(Number(ppk));

  const statusCircleColor = hasCpkData ? cpkColor(Number(cpk)) : NO_DATA_COLOR;
  const statusCircleColorPPK = hasPpkData ? cpkColor(Number(ppk)) : NO_DATA_COLOR;
  const giveawayOk = (giveawayPct ?? 0) <= (giveawayTarget ?? 1);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 100px)' },
        minHeight: { xs: '100vh', md: 'unset' },
        bgcolor: '#0a0a0a',
        color: '#fff',
        fontFamily: FONT,
        overflow: { xs: 'auto', md: 'hidden' },
        userSelect: 'none',
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1,
          borderBottom: '1px solid #1a1a1a',
          bgcolor: '#000',
          minHeight: 48,
          flexShrink: 0,
        }}
      >
        {/* Left: logo + live badge + plant */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: { xs: 16, md: 22 },
              fontWeight: 'bold',
              color: '#f72585',
              letterSpacing: 1,
            }}
          >
            Sigma Track
          </Typography>

          {/* LIVE badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              bgcolor: '#0d2b0d',
              border: '1px solid #1e5c1e',
              borderRadius: '20px',
              px: 1.2,
              py: 0.3,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#4caf50',
                boxShadow: '0 0 6px #4caf50',
              }}
            />
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#4caf50', fontWeight: 'bold' }}>
              LIVE
            </Typography>
          </Box>

          <Typography sx={{ fontFamily: FONT, fontSize: { xs: 12, md: 16 }, color: '#ccc' }}>
            {plantName}
          </Typography>
        </Box>

        {/* Right: date/time */}
        <Typography sx={{ fontFamily: FONT, fontSize: { xs: 11, md: 15 }, color: '#999', letterSpacing: 1 }}>
          {formatDateTime(now)}
        </Typography>
      </Box>

      {/* ── BODY ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, gap: 1, p: 1, overflow: { xs: 'visible', md: 'hidden' } }}>

        {/* ── LEFT PANEL ── */}
        <Box
          sx={{
            width: { xs: '100%', md: '46%' },
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            overflow: { xs: 'visible', md: 'hidden' },
          }}
        >
          {/* Line + SKU */}
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  bgcolor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  minWidth: 72,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 'bold', color: '#fff' }}>
                  {lineName}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1.5 }}>
                  SKU INFORMATION
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                  {skuLabel}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#999' }}>
                    Shift&nbsp;<span style={{ color: '#aaa' }}>{shift || '—'}</span>
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#999' }}>
                    Date&nbsp;<span style={{ color: '#aaa' }}>{factoryDate || '—'}</span>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </SectionCard>

          {/* Process Status */}
          <SectionCard sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1.5 }}>
                PROCESS STATUS
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#bbb' }}>
                {timeRange}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Status circle */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: avgStatusColor,
                  boxShadow: hasHistogramData
                    ? `0 0 16px ${avgStatusColor}66`
                    : 'none',
                  flexShrink: 0,
                }}
              />

              {/* Avg weight */}
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#bbb', letterSpacing: 1 }}>
                  Avg.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: 56, fontWeight: 'bold', color: avgStatusColor, lineHeight: 1 }}
                  >
                    {avgWeight > 0 ? avgWeight.toFixed(1) : '--'}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 22, color: hasHistogramData ? '#888' : NO_DATA_COLOR, }}>g</Typography>
                </Box>
              </Box>

              {/* Sigma (5-min) */}
              <Box sx={{ ml: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#bbb', letterSpacing: 1 }}>
                  σ (5min)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: 38, fontWeight: 'bold', color: '#ccc', lineHeight: 1 }}
                  >
                    {sigma5.toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#888' }}>g</Typography>
                </Box>
              </Box>
            </Box>
          </SectionCard>

          {/* LSL / Std / USL */}
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SpecCell label="LSL" value={lsl} />
              <Box sx={{ width: '1px', bgcolor: '#222', alignSelf: 'stretch', mx: 1 }} />
              <SpecCell label="Std." value={lsl} />
              <Box sx={{ width: '1px', bgcolor: '#222', alignSelf: 'stretch', mx: 1 }} />
              <SpecCell label="USL" value={usl} />
            </Box>
          </SectionCard>

          {/* Giveaway */}
          <SectionCard accent={giveawayOk ? '#4caf50' : '#f44336'}>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1.5, mb: 0.8 }}>
              GIVEAWAY (5 min)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#bbb' }}>Act.</Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 44,
                      fontWeight: 'bold',
                      color: giveawayOk ? '#4caf50' : '#f44336',
                      lineHeight: 1,
                    }}
                  >
                    {giveawayPct.toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 16, color: giveawayOk ? '#4caf50' : '#f44336' }}>
                    %
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa' }}>
                  Target {giveawayTarget}%
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  sx={{ fontFamily: FONT, fontSize: 44, fontWeight: 'bold', color: '#fff', lineHeight: 1 }}
                >
                  {giveawayKg.toFixed(2)}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#888' }}>Kg.</Typography>
              </Box>
            </Box>
          </SectionCard>

          {/* Production */}
          <SectionCard>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1.5, mb: 0.8 }}>
              PRODUCTION (5 min) Pack
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Total */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 52, fontWeight: 'bold', color: '#fff', lineHeight: 1 }}>
                  {totalPack}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#bbb' }}>Pack</Typography>
              </Box>

              <Box sx={{ width: '1px', bgcolor: '#222', alignSelf: 'stretch' }} />

              {/* Breakdown */}
              <Box sx={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'space-around' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1 }}>
                    On spec
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 36, fontWeight: 'bold', color: '#fff', lineHeight: 1.1 }}>
                    {onSpecPack}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1 }}>
                    Upper
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 36, fontWeight: 'bold', color: '#fff', lineHeight: 1.1 }}>
                    {upperPack}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1 }}>
                    Detected
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 36,
                      fontWeight: 'bold',
                      color: detectedPack > 0 ? '#f44336' : '#4caf50',
                      lineHeight: 1.1,
                    }}
                  >
                    {detectedPack}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </SectionCard>
        </Box>

        {/* ── RIGHT PANEL ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, overflow: { xs: 'visible', md: 'hidden' }, minWidth: 0 }}>

          {/* SPC Chart */}
          <SectionCard sx={{ flex: '0 0 auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#aaa', letterSpacing: 1.5 }}>
                SPC &amp; PROCESS CAP.
              </Typography>
              <BaselineLogButton params={baselineParams} label={baselineLabel} currentShiftCount={baselineCurrentShiftCount} />
            </Box>
            <SPCChart
              data={spcData}
              categories={spcCategories}
              ucl={ucl}
              lcl={lcl}
              mean={meanX}
              height={isSmall ? 160 : 220}
            />
          </SectionCard>

          {/* Histogram + Cpk/Ppk row */}
          <Box sx={{ display: 'flex', gap: 1, flex: { xs: 'none', md: 1 }, overflow: { xs: 'visible', md: 'hidden' } }}>

            {/* Histogram */}
            <SectionCard sx={{ flex: 1, overflow: 'hidden' }}>
              <HistogramChart
                histData={histData}
                // actualCurve={actualCurve}
                // normalCurve={normalCurve}
                actualCurve={smoothWithinCurve}
                normalCurve={smoothOverallCurve}
                lsl={lsl}
                usl={usl}
                avg={avgWeight}
                lslLabel="LSL"
                uslLabel="USL"
                avgLabel="Avg"
                labelBgColor="#1a1a1a"
                labelTextColor="#ff6b6b"
                width='1000%'
                height={isSmall ? 150 : 220}
              />
            </SectionCard>

            {/* Cpk / Ppk */}
            <SectionCard sx={{ width: 170, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', bgcolor: '#0a0a0a' }}>
              <CapabilityBlock label="Cpk" value={hasCpkData ? Number(cpk) : null} target={cpkTarget} color={statusCircleColor} />
              <Box sx={{ height: '1px', bgcolor: '#222', width: '80%', mb: 1.5 }} />
              <CapabilityBlock label="Ppk" value={hasPpkData ? Number(ppk) : null} target={ppkTarget} color={statusCircleColorPPK} />
            </SectionCard>
          </Box>

          {/* {showSuggestionWeb && !!suggestionWeb && (
            <SectionCard
              sx={{
                flexShrink: 0,
                border: `1px solid ${statusCircleColor}66`,
                bgcolor: '#0f0c00',
                minHeight: 52,
              }}
            >
              <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#e0c060' }}>
                {suggestionWeb}
              </Typography>
            </SectionCard>
          )} */}
          {showSuggestionWeb &&
            Array.isArray(suggestionWeb) &&
            suggestionWeb.length > 0 && (
              <SectionCard
                sx={{
                  flexShrink: 0,
                  border: `1px solid ${statusCircleColor}66`,
                  bgcolor: '#0f0c00',
                  minHeight: 52,
                }}
              >
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5, // ระยะห่างระหว่างแต่ละรายการ
                  }}
                >
                  {suggestionWeb.map((suggestion, index) => (
                    <Typography
                      component="li"
                      key={`${suggestion}-${index}`}
                      sx={{
                        fontFamily: FONT,
                        fontSize: 14,
                        color: '#e0c060',
                        lineHeight: 1,
                      }}
                    >
                      {suggestion}
                    </Typography>
                  ))}
                </Box>
              </SectionCard>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default SigmaTrackGiveawayPage1;
